import {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactNode,
  type TouchEventHandler,
} from "react";

import clsx from "clsx";
import { uniq } from "lodash";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { LoadingSlideAtom } from "@components/title-slide-view";
import { useQueryMetadata } from "@components/v2/modals";
import { useOverviewContext } from "@features/overview-v2/hooks";
import {
  TileType,
  gridIsReady,
  type TileDescriptor,
} from "@features/overview-v2/types";
import { GET_OVERVIEW_METRICS_REPORT_V2 } from "@gql/overview-v2";
import { useNorthbeamLazyQuery } from "@hooks/http";
import {
  type GetOverviewMetricsReportV2,
  type GetOverviewMetricsReportV2Variables,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";

import { Content } from "./content";
import { ContentError } from "./content-error";
import { DeleteCard } from "./delete-card";

import "./index.scss";

interface TileWrapperProps {
  readonly children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export interface TileProps extends Omit<TileWrapperProps, "children"> {
  onMouseDown?: MouseEventHandler;
  onMouseUp?: MouseEventHandler;
  onTouchEnd?: TouchEventHandler;
  tile: TileDescriptor;
  isResizing: boolean;
}

const TileWrapper = forwardRef<HTMLDivElement, TileWrapperProps>(
  function TileWrapper({ style, children, className }, ref): JSX.Element {
    return (
      <div
        ref={ref}
        style={style}
        className={clsx(
          "tile grid grid-cols-[0px_auto_24px] grid-rows-[0px_auto_24px] hover:z-[3]",
          "group/grid-item",
          className,
        )}
      >
        {children}
      </div>
    );
  },
);

export const Tile = forwardRef<HTMLDivElement, PropsWithChildren<TileProps>>(
  (
    {
      style,
      className,
      onMouseDown,
      onMouseUp,
      onTouchEnd,
      tile,
      isResizing,
      children,
    },
    ref,
  ) => {
    const { addQueryMetadata } = useQueryMetadata();
    const [aboutToDelete, setAboutToDelete] = useState(false);

    const {
      metrics,
      grid,
      filters: {
        attributionModel,
        accountingMode,
        attributionWindow,
        level,
        timeGranularity,
        dateRangeInterval,
        compareDateRangeInterval,
      },
    } = useOverviewContext();

    const metric1 = useMemo(
      () => metrics.find((e) => e.id === tile.metricId),
      [tile, metrics],
    );

    const metric2 = useMemo(
      () => metrics.find((e) => e.id === tile.metric2Id),
      [tile, metrics],
    );

    const {
      data: { data: metric1Data, loading: metric1DataLoading },
      loadData: loadMetric1Data,
    } = useNorthbeamLazyQuery<
      GetOverviewMetricsReportV2,
      GetOverviewMetricsReportV2Variables
    >(GET_OVERVIEW_METRICS_REPORT_V2);

    const {
      data: { data: metric2Data, loading: metric2DataLoading },
      loadData: loadMetric2Data,
    } = useNorthbeamLazyQuery<
      GetOverviewMetricsReportV2,
      GetOverviewMetricsReportV2Variables
    >(GET_OVERVIEW_METRICS_REPORT_V2);

    useEffect(() => {
      if (metric1Data?.me?.overviewMetricsReportV2?.queryMetadata?.bqLogs) {
        addQueryMetadata({
          context: "Overview",
          topic:
            tile.name ||
            metric1?.metadata.overviewPageMetricName ||
            metric1?.name ||
            "1st Metric",
          metadata: metric1Data.me.overviewMetricsReportV2.queryMetadata,
        });
      }
    }, [
      metric1Data?.me?.overviewMetricsReportV2?.queryMetadata,
      tile,
      metric1,
    ]);

    useEffect(() => {
      if (metric2Data?.me?.overviewMetricsReportV2?.queryMetadata?.bqLogs) {
        addQueryMetadata({
          context: "Overview",
          topic:
            tile.name ||
            metric2?.metadata.overviewPageMetricName ||
            metric2?.name ||
            "2nd Metric",
          metadata: metric2Data.me.overviewMetricsReportV2.queryMetadata,
        });
      }
    }, [
      metric2Data?.me?.overviewMetricsReportV2?.queryMetadata,
      tile,
      metric2,
    ]);

    const { value: breakdownConfigs } = useSalesBreakdownConfigs();

    useEffect(() => {
      if (!metric1) {
        return;
      }

      const resolveBaseMetrics = (metric: SalesPageMetric) =>
        metric.baseMetrics
          ? metric.baseMetrics.flatMap((e) => e.requiredBaseMetrics)
          : metric.requiredBaseMetrics;

      const getAttributionWindows = (metric: SalesPageMetric) => {
        const res = metric.metadata.isLtv ? ["infinity"] : [attributionWindow];

        if (
          metric.baseMetrics &&
          metric.baseMetrics.some((e) => e.metadata.isLtv)
        ) {
          res.push("infinity");
        }

        return uniq(res);
      };

      const common = {
        attributionMethod: attributionModel,
        accountingMode,
        timeGranularity,
        level,
        breakdowns: tile.breakdowns,
        breakdownFilters: filterUnavailableBreakdowns({
          breakdownFilters: tile.breakdownFilters ?? [],
          breakdownConfigs,
        }),
        aggregateAll: tile.type === "metric",
        disableDateCompression: true,
      };

      const isConversionLag = tile.type === TileType.CONVERSION_LAG;
      const hasCompare = compareDateRangeInterval && !isConversionLag;

      loadMetric1Data({
        variables: {
          ...common,
          attributionWindow: metric1.metadata.isLtv
            ? "infinity"
            : attributionWindow,
          attributionWindows: getAttributionWindows(metric1),
          dateRange: dateRangeInterval,
          compareDateRange: hasCompare ? compareDateRangeInterval : null,
          metrics: resolveBaseMetrics(metric1),
          filters: tile.filters,
          conversionLag: isConversionLag,
        },
      });

      if (metric2 && !isConversionLag) {
        loadMetric2Data({
          variables: {
            ...common,
            attributionWindow: metric2.metadata.isLtv
              ? "infinity"
              : attributionWindow,
            attributionWindows: getAttributionWindows(metric2),
            dateRange: dateRangeInterval,
            compareDateRange: compareDateRangeInterval,
            metrics: resolveBaseMetrics(metric2),
            filters: tile.metric2Filters,
          },
        });
      }
    }, [
      loadMetric1Data,
      loadMetric2Data,
      metric1,
      metric2,
      tile,
      attributionModel,
      accountingMode,
      attributionWindow,
      breakdownConfigs,
      level,
      timeGranularity,
      dateRangeInterval,
      compareDateRangeInterval,
    ]);

    const DraggableHandler = useMemo(
      () => () =>
        (
          <div
            onMouseDown={(e) => {
              if (!aboutToDelete) {
                onMouseDown?.(e);
              }
            }}
            onMouseUp={(e) => {
              if (!aboutToDelete) {
                onMouseUp?.(e);
              }
            }}
            onTouchEnd={(e) => {
              if (!aboutToDelete) {
                onTouchEnd?.(e);
              }
            }}
          >
            <i
              className={clsx(
                "fa-solid fa-grip-vertical",
                "absolute left-2.5 top-6 opacity-0 drag-indicator-icon text-gray mr-[4px] text-xs",
                "hover:cursor-grab group-hover/content:opacity-100",
              )}
            />
          </div>
        ),
      [aboutToDelete, onMouseDown, onMouseUp, onTouchEnd],
    );

    const isResizable = gridIsReady(grid)
      ? tile.type === TileType.METRIC ||
        tile.layouts[grid.breakpoint].x !== grid.numColumns - 1
      : false;

    if (metric1DataLoading || metric2DataLoading || !metric1 || !metric1Data) {
      return (
        <TileWrapper className={className} style={style} ref={ref}>
          <div
            className={clsx(
              "react-grid-item-content group/content overflow-hidden",
              "col-start-2 row-start-2 relative border border-v2-secondary rounded-lg py-4 px-6",
              {
                "bg-mars-2": aboutToDelete,
                "bg-v2-primary hover:border-v2-primary": !aboutToDelete,
              },
            )}
          >
            <DraggableHandler />
            {metric1DataLoading || metric2DataLoading ? (
              <LoadingSlideAtom className="w-32" />
            ) : (
              <>
                {aboutToDelete ? (
                  <DeleteCard
                    tile={tile}
                    onCancel={() => setAboutToDelete(false)}
                  />
                ) : (
                  <ContentError
                    tile={tile}
                    onAboutToDelete={() => setAboutToDelete(true)}
                  />
                )}
              </>
            )}
          </div>
        </TileWrapper>
      );
    }

    return (
      <TileWrapper className={className} style={style} ref={ref}>
        <div
          className={clsx(
            "react-grid-item-content group/content overflow-hidden",
            "col-start-2 row-start-2 relative border border-v2-secondary rounded-lg py-4",
            {
              "bg-mars-2": aboutToDelete,
              "bg-v2-primary hover:border-v2-primary": !aboutToDelete,
            },
          )}
        >
          <DraggableHandler />
          {aboutToDelete ? (
            <div className="px-6">
              <DeleteCard
                tile={tile}
                onCancel={() => setAboutToDelete(false)}
              />
            </div>
          ) : (
            <Content
              tile={tile}
              isResizing={isResizing}
              metric1={metric1}
              metric2={metric2}
              metric1Data={metric1Data.me.overviewMetricsReportV2.mainData}
              metric1CompareData={
                metric1Data.me.overviewMetricsReportV2.compareData ?? undefined
              }
              metric2Data={
                metric2Data?.me.overviewMetricsReportV2.mainData ?? undefined
              }
              metric2CompareData={
                metric2Data?.me.overviewMetricsReportV2.compareData ?? undefined
              }
              onAboutToDelete={() => setAboutToDelete(true)}
            />
          )}
        </div>
        {isResizable && !aboutToDelete && (
          <div
            className={clsx("react-resizable-gutter-wrapper group/gutter", {
              "col-start-3 row-start-2 pl-[8px]": tile.type !== TileType.METRIC,
              "col-start-2 row-start-3 pt-[8px]": tile.type === TileType.METRIC,
            })}
          >
            <div
              className={clsx(
                "bg-cosmic-2 rounded-lg flex opacity-0 group-hover/gutter:opacity-100",
                "transition-opacity ease-in-out duration-100",
                "group-hover/gutter:duration-300 group-hover/gutter:delay-0",
                {
                  "react-resizable-gutter-right h-full w-2 items-center":
                    tile.type !== TileType.METRIC,
                  "react-resizable-gutter-bottom h-2 w-full justify-center":
                    tile.type === TileType.METRIC,
                },
              )}
            >
              {children}
            </div>
          </div>
        )}
      </TileWrapper>
    );
  },
);

/*
 * This function filters out breakdown filters that are not available in the breakdownConfigs.
 * For example "Amazon - Ads and Organic" is not an available breakdown filter for _every_ client.
 */
const filterUnavailableBreakdowns = ({
  breakdownFilters,
  breakdownConfigs,
}: {
  breakdownFilters: string[][];
  breakdownConfigs: CategoricalBreakdownConfig[];
}) => {
  const availableBreakdownFiltersSet = new Set(
    breakdownConfigs
      .map(({ choices }) => choices.map(({ value }) => value))
      .flat(),
  );

  return breakdownFilters.filter((filter) =>
    filter.every((value) => availableBreakdownFiltersSet.has(value)),
  );
};
